import { useEffect } from 'react';
import { useWatch } from 'react-hook-form';
import { SetOptional } from 'type-fest';

import { TypeaheadSelectInputOption } from '@/components/form/baseInputs/inputTypes';
import {
  FormModal,
  FormModalProps,
} from '@/components/modals/FormModal/FormModal';
import { FormModalActions } from '@/components/modals/FormModal/FormModalActions/FormModalActions';
import {
  FeedbackMessages,
  useFeedback,
} from '@/components/notifications/Feedback/useFeedback';
import { FormProvider, useFormContext } from '@/components/react-hook-form';
import { useForm, useSubmitSuccessHandler } from '@/components/react-hook-form';
import { useFormSaveHandler } from '@/hooks/useFormSaveHandler';
import { useReportError } from '@/hooks/useReportError';
import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';
import { DocumentType, SummarizeDocumentInput } from '@/types/schema';

import { Operations as DocumentDetailsOperations } from '../graphql/DocumentDetails.generated';
import { useSummarizeDocumentMutation } from './graphql/RegenerateSummaryModal.generated';
import { useSummaryOptions } from './hooks/useSummaryOptions';
import {
  defaultValues,
  RegenerateSummaryModalFormShape,
} from './RegenerateSummaryModal.constants';
import { RegenerateSummaryModalForm } from './RegenerateSummaryModalForm';
import { getDefaultSummaryNameForPrompt } from './utils/RegenerateSummaryModalForm.utils';

type FormModalOptionalProps = SetOptional<FormModalProps, 'heading'>;

export interface RegenerateSummaryModalProps extends FormModalOptionalProps {
  documentId: string;
  documentType: DocumentType;
  currentSummaryId?: string;
  currentSummaryName?: string;
  currentSummaryPromptId?: string;
  onSuccess?: () => void;
  openedFromAddNew?: boolean;
  setTransitionToNewSummaryWhenAvailable: (value: boolean) => void;
}

interface RegenerateSummaryModalInnerProps extends RegenerateSummaryModalProps {
  summaryOptions: TypeaheadSelectInputOption<string>[];
  promptOptions: TypeaheadSelectInputOption<string>[];
}

function RegenerateSummaryModalInner({
  onClose,
  heading,
  documentId,
  documentType,
  currentSummaryId,
  currentSummaryName,
  onSuccess,
  setTransitionToNewSummaryWhenAvailable,
  openedFromAddNew,
  summaryOptions,
  promptOptions,
  ...formModalProps
}: RegenerateSummaryModalInnerProps) {
  const modalHeading =
    heading ?? (openedFromAddNew ? 'Create new summary' : 'Regenerate summary');
  const buttonText = openedFromAddNew ? 'Create summary' : 'Regenerate summary';

  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const { formRef, handleSave } = useFormSaveHandler();
  const trackUserEvent = useTrackUserEvent();
  const { handleSubmit, reset, shouldBlockNavigation, formState } =
    useFormContext<RegenerateSummaryModalFormShape>();
  const regenerateAsNew = useWatch<RegenerateSummaryModalFormShape>({
    name: 'regenerateAsNew',
  });

  const [summarizeDocumentMutation, { loading }] = useSummarizeDocumentMutation(
    {
      refetchQueries: [DocumentDetailsOperations.Query.DocumentDetails],
      awaitRefetchQueries: true,
      onError: (error) => {
        showFeedback(FeedbackMessages.formSaveError);
        reportError(`Could not regenerate summary`, error);
      },
      onCompleted: () => {
        showFeedback('Summary regeneration started successfully', {
          variant: 'success',
        });
      },
    }
  );

  const onSubmit = handleSubmit((formData) => {
    const input: SummarizeDocumentInput = {
      documentID: documentId,
      includeAssociatedDocuments: formData.incorporateAllDocuments,
    };

    // Only pass summaryDisplayName when creating a new summary. When
    // regenerating in place, omitting this field preserves the existing
    // summary's title — passing an empty string would otherwise clobber it.
    if (formData.regenerateAsNew) {
      input.summaryDisplayName = formData.summaryName.trim();
    }

    // Only pass updateDocumentSummaryID if NOT regenerating as new
    if (!formData.regenerateAsNew && currentSummaryId) {
      input.updateDocumentSummaryID = currentSummaryId;
    }

    // Add additional details if provided
    if (formData.additionalDetails.trim()) {
      input.additionalSummaryDetailsPrompt = formData.additionalDetails;
    }

    // Add format reference if provided
    if (formData.formatReferenceSummaryId) {
      input.formatReferenceSummaryID = formData.formatReferenceSummaryId;
    }

    if (formData.summaryPromptId) {
      input.summaryPromptID = formData.summaryPromptId;
    }

    trackUserEvent('document summary regeneration initiated', {
      regenerateAsNew: formData.regenerateAsNew,
      hasAdditionalDetailsPrompt: Boolean(input.additionalSummaryDetailsPrompt),
      doesReferenceSummaryForFormatting: Boolean(
        input.formatReferenceSummaryID
      ),
    });

    return summarizeDocumentMutation({
      variables: { input },
    });
  });

  const closeModal = () => {
    reset();
    onClose();
  };

  useSubmitSuccessHandler(() => {
    // if the user is generating a new summary, we want to
    // automatically select it when it is available
    if (regenerateAsNew) {
      setTransitionToNewSummaryWhenAvailable(true);
    }

    onSuccess?.();
    closeModal();
  });

  return (
    <FormModal
      {...formModalProps}
      onClose={onClose}
      heading={modalHeading}
      shouldBlockClose={shouldBlockNavigation}
      actions={
        <FormModalActions.Provider formState={formState}>
          <FormModalActions.CancelButton onClick={closeModal} />
          <FormModalActions.SaveButton onClick={handleSave} disabled={loading}>
            {buttonText}
          </FormModalActions.SaveButton>
        </FormModalActions.Provider>
      }
    >
      <form ref={formRef} onSubmit={onSubmit} noValidate>
        <RegenerateSummaryModalForm
          summaryOptions={summaryOptions}
          promptOptions={promptOptions}
          currentSummaryName={currentSummaryName}
          documentType={documentType}
          openedFromAddNew={openedFromAddNew}
        />
      </form>
    </FormModal>
  );
}

export const RegenerateSummaryModal = ({
  currentSummaryName,
  currentSummaryPromptId,
  openedFromAddNew,
  ...props
}: RegenerateSummaryModalProps) => {
  const { currentSummaryId, documentType } = props;

  const { summaryOptions, promptOptions } = useSummaryOptions(
    documentType,
    currentSummaryId
  );
  const initialSummaryPromptId =
    currentSummaryPromptId ?? promptOptions[0]?.value ?? '';

  // Set initial values based on context
  const initialValues = {
    ...defaultValues,
    summaryName: getDefaultSummaryNameForPrompt(
      promptOptions,
      initialSummaryPromptId
    ),
    regenerateAsNew: true,
    summaryPromptId: initialSummaryPromptId,
  };

  const methods = useForm<RegenerateSummaryModalFormShape>({
    defaultValues: initialValues,
  });
  const { resetField } = methods;

  // Keep the field aligned with the current summary's source prompt when available.
  // If there is no linked prompt, fall back to the first available option.
  useEffect(() => {
    if (currentSummaryPromptId || promptOptions.length > 0) {
      resetField('summaryPromptId', {
        defaultValue: currentSummaryPromptId ?? promptOptions[0]?.value ?? '',
      });
    }
  }, [currentSummaryPromptId, promptOptions, resetField]);

  return (
    <FormProvider {...methods}>
      <RegenerateSummaryModalInner
        {...props}
        currentSummaryName={currentSummaryName}
        openedFromAddNew={openedFromAddNew}
        summaryOptions={summaryOptions}
        promptOptions={promptOptions}
      />
    </FormProvider>
  );
};
